import { useEffect, useMemo } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { ChevronDown } from "lucide-react"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { useActivitiesFilter } from "@/modules/study/stores/use-activities-filter-store"
import { isSunday, parseISO } from "date-fns"
import type { Activities } from "@/modules/study/entities/activities"
import useCoursesInfo from "@/modules/courses/hooks/use-courses-info"

const getInitial = (name: string) => {
  const words = name.split(" ")
  return words
    .map((w) => w.charAt(0).toUpperCase())
    .join("")
    .slice(0, 1)
}

interface WeeklyActivitiesFilterProps {
  activities: Activities[]
}

export default function WeeklyActivitiesFilter({ activities }: WeeklyActivitiesFilterProps) {
  const { getCourseColor } = useCoursesInfo()

  const subjects = useMemo(
    () =>
      activities
        .filter((activity) => !isSunday(parseISO(activity.date)))
        .map((activity) => activity.activities)
        .flat()
        .map((subject) => subject.subject),
    [activities],
  )

  const uniqueSubjects = useMemo(
    () => Array.from(new Map(subjects.map((subj) => [subj.id, subj])).values()),
    [subjects],
  )

  const { filteredSubjects, setFilteredSubjects } = useActivitiesFilter()

  useEffect(() => {
    setFilteredSubjects(uniqueSubjects)
  }, [subjects, activities])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="ml-8 border-0 text-base" variant="outline">
          Materias <ChevronDown className="ml-4 !size-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        {uniqueSubjects.map((subject) => (
          <DropdownMenuCheckboxItem
            key={subject.id}
            checked={filteredSubjects.some((filtered) => filtered.id === subject.id)}
            onCheckedChange={(isChecked) => {
              const news = isChecked
                ? [...filteredSubjects, subject]
                : filteredSubjects.filter((prev) => prev.id !== subject.id)

              setFilteredSubjects(news)
            }}
            className="flex items-center gap-2"
          >
            <Avatar className="h-6 w-6">
              <AvatarFallback
                style={{
                  background: getCourseColor(subject.id),
                }}
                className="text-xs font-medium text-white"
              >
                {getInitial(subject.name)}
              </AvatarFallback>
            </Avatar>
            {subject.name}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
